/**
 * Dynamic Style Memory
 * Tracks user stylistic preferences per session and auto-adapts.
 * Session-based only — resets on page reload.
 */

import { GlobalSettings } from './engineTypes';

export interface StyleTendency {
  id: string;
  label: string;
  score: number;    // 0-100, higher = stronger preference
  active: boolean;  // score > threshold
}

interface StyleSignal {
  id: string;
  label: string;
  detect: (gs: GlobalSettings, style: string, scene: string) => boolean;
}

const STYLE_SIGNALS: StyleSignal[] = [
  {
    id: 'dramatic_lighting',
    label: 'Dramatic Lighting',
    detect: (gs) => gs.contrastCurve > 65 || gs.hdrControl > 65,
  },
  {
    id: 'shallow_dof',
    label: 'Shallow Depth of Field',
    detect: (_gs, _style, _scene) => false, // detected from elements, handled below
  },
  {
    id: 'teal_orange',
    label: 'Teal & Orange Grade',
    detect: (gs) => gs.tealOrangePreset,
  },
  {
    id: 'cyberpunk_neon',
    label: 'Cyberpunk / Neon',
    detect: (_gs, style, scene) =>
      ['neon_cyberpunk', 'cyberpunk_ultra', 'neo_noir'].includes(style) ||
      /\b(neon|cyberpunk|سايبر|نيون)\b/i.test(scene),
  },
  {
    id: 'film_vintage',
    label: 'Vintage Film Look',
    detect: (gs) => gs.vintageMode || gs.filmGrain || (gs.filmStock !== 'none'),
  },
  {
    id: 'high_detail',
    label: 'Ultra Detail',
    detect: (gs) => gs.microDetail > 70 && gs.sharpness > 70,
  },
  {
    id: 'atmospheric',
    label: 'Heavy Atmosphere',
    detect: (gs) => gs.fogHaze > 40 || gs.cinematicSmoke > 40 || gs.atmosphericDensity > 40,
  },
  {
    id: 'beauty_retouch',
    label: 'Beauty Retouching',
    detect: (gs) => gs.skinSmoothing > 30 || gs.beautyPreset !== 'none',
  },
  {
    id: 'dark_moody',
    label: 'Dark & Moody',
    detect: (_gs, style, scene) =>
      ['noir', 'neo_noir', 'moody_noir', 'dark_fantasy', 'gothic_horror'].includes(style) ||
      /\b(dark|مظلم|قاتم|horror|رعب|moody)\b/i.test(scene),
  },
  {
    id: 'epic_scale',
    label: 'Epic Scale',
    detect: (_gs, style, scene) =>
      ['mythic_epic', 'fantasy', 'volcanic_fire'].includes(style) ||
      /\b(epic|ملحمي|grand|عظيم|massive|ضخم)\b/i.test(scene),
  },
];

const DECAY_RATE = 0.85;  // Fade factor per generation when signal absent
const BOOST_RATE = 15;    // Points added when signal detected
const ACTIVE_THRESHOLD = 30;

// Session-scoped scores
let scores: Record<string, number> = {};
let generationCount = 0;

/**
 * Reset style memory (on app init or explicit reset).
 */
export function resetStyleMemory() {
  scores = {};
  generationCount = 0;
}

/**
 * Update style memory with current settings snapshot.
 * Call this on every prompt generation.
 */
export function updateStyleMemory(
  gs: GlobalSettings,
  stylePreset: string,
  sceneDescription: string
) {
  generationCount++;

  for (const signal of STYLE_SIGNALS) {
    const currentScore = scores[signal.id] ?? 0;
    const detected = signal.detect(gs, stylePreset, sceneDescription);

    if (detected) {
      // Boost score (capped at 100)
      scores[signal.id] = Math.min(100, currentScore + BOOST_RATE);
    } else {
      // Decay gradually
      scores[signal.id] = currentScore * DECAY_RATE;
      if (scores[signal.id] < 2) scores[signal.id] = 0;
    }
  }
}

/**
 * Get current style tendencies sorted by strength.
 */
export function getStyleTendencies(): StyleTendency[] {
  return STYLE_SIGNALS.map(signal => ({
    id: signal.id,
    label: signal.label,
    score: Math.round(scores[signal.id] ?? 0),
    active: (scores[signal.id] ?? 0) >= ACTIVE_THRESHOLD,
  }))
    .filter(t => t.score > 0)
    .sort((a, b) => b.score - a.score);
}

/**
 * Get prompt enrichment suggestions based on learned preferences.
 * Only returns suggestions for strong tendencies that aren't already
 * explicitly set in the current settings.
 */
export function getStyleMemoryEnrichments(gs: GlobalSettings): string[] {
  const tendencies = getStyleTendencies().filter(t => t.active);
  const enrichments: string[] = [];

  for (const t of tendencies) {
    switch (t.id) {
      case 'dramatic_lighting':
        if (gs.contrastCurve <= 60) enrichments.push('dramatic high-contrast lighting');
        break;
      case 'teal_orange':
        if (!gs.tealOrangePreset) enrichments.push('cinematic teal and orange color grading');
        break;
      case 'cyberpunk_neon':
        enrichments.push('neon-lit cyberpunk atmosphere');
        break;
      case 'film_vintage':
        if (!gs.vintageMode) enrichments.push('vintage analog film texture');
        break;
      case 'atmospheric':
        if (gs.fogHaze <= 30) enrichments.push('atmospheric haze and depth');
        break;
      case 'dark_moody':
        enrichments.push('dark moody undertones');
        break;
      case 'epic_scale':
        enrichments.push('epic grandeur and scale');
        break;
    }
  }

  // Cap enrichments to avoid over-influencing
  return enrichments.slice(0, 2);
}

/**
 * Get the session generation count.
 */
export function getGenerationCount(): number {
  return generationCount;
}
